// plugins/translation.js
import { defineNuxtPlugin } from '#app';
import translations from '~/utils/translations/globalTranslations';
import { isParticipantFacingRoute, isParticipantTaskRoute } from '~/utils/participantRoute';
import {
    PARTICIPANT_TASK_TRANSLATION_NAMESPACES,
    resolveParticipantPortalLocale,
    resolveParticipantTaskLocale,
} from '~/utils/participantUiLocale';

export default defineNuxtPlugin((nuxtApp) => {
    const getValidLanguage = (lang) => {
        return typeof lang === 'string' && lang.trim() ? lang : null;
    };

    const normalizeLanguage = (lang) => {
        const valid = getValidLanguage(lang);

        if (!valid) {
            return null;
        }

        return valid === 'pt-PT' ? 'pt' : valid;
    };

    const resolveParticipantTranslationLocale = ({
        namespace,
        route,
        store,
        auth,
    }) => {
        const onTaskRoute = isParticipantTaskRoute(route);
        const task = store.getters['tasks/task'];
        const portalLocale = resolveParticipantPortalLocale({
            user: auth.user,
            query: route.query,
        });

        if (namespace === 'ParticipantPortal') {
            return normalizeLanguage(portalLocale) || 'en';
        }

        if (onTaskRoute && task?.language && PARTICIPANT_TASK_TRANSLATION_NAMESPACES.has(namespace)) {
            const taskLocale = resolveParticipantTaskLocale({
                query: route.query,
                task,
            });

            return normalizeLanguage(taskLocale) || 'en';
        }

        return normalizeLanguage(portalLocale) || 'en';
    };

    const t = (key, params) => {
        let keys, explicitLang, namespace;

        if (key.includes('|')) {
            const [keyNamespace, subKey, keyLang] = key.split('|');
            explicitLang = keyLang;
            namespace = keyNamespace;
            keys = [keyNamespace, ...subKey.split('.')];
        } else {
            keys = key.split('.');
            namespace = keys[0];
        }

        const store = nuxtApp.vueApp.config.globalProperties.$store;
        const auth = nuxtApp.vueApp.config.globalProperties.$auth;
        const route = nuxtApp.$router?.currentRoute?.value;

        let language = 'en';

        if (!explicitLang && route && isParticipantFacingRoute(route)) {
            language = resolveParticipantTranslationLocale({
                namespace,
                route,
                store,
                auth,
            });
        } else if (!explicitLang) {
            const task = store.getters['tasks/task'];
            const hasTask = task?.slug;

            language = normalizeLanguage(
                hasTask
                    ? task.language
                    : auth.user?.locale
            ) || 'en';
        }

        const forumLanguage = getValidLanguage(
            store.state.discussionForum?.discussionForumOverview?.data?.language
        );

        const currentLang =
            normalizeLanguage(explicitLang) ||
            (namespace === 'DiscussionForums' && forumLanguage ? normalizeLanguage(forumLanguage) : null) ||
            language ||
            'en';

        let dict = translations[currentLang] || translations.en;
        const englishDict = translations.en;

        const lookup = (obj, path) => {
            let res = obj;
            for (const p of path) {
                if (res && res[p] != null) {
                    res = res[p];
                } else {
                    return null;
                }
            }
            return res;
        };

        let result = lookup(dict, keys);
        if ((result == null) && currentLang !== 'en') {
            console.warn(
                `[Translation] "${key}" not found in "${currentLang}", falling back to "en".`
            );
            result = lookup(englishDict, keys);
        }

        if (params && typeof result === 'string') {
            Object.keys(params).forEach((param) => {
                const re = new RegExp(`{${param}}`, 'g');
                result = result.replace(re, params[param]);
            });
        }

        return result || '';
    };

    nuxtApp.vueApp.config.globalProperties.$t = t;
    nuxtApp.provide('t', t);
});
